/**
 * Entity Record Form Component
 *
 * Dynamic form for creating/editing entity records using DynamicForm from design system.
 */

import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  DynamicForm,
  type FieldDefinition,
  GlassAlert,
  GlassButton,
  GlassCard,
  ShimmerLoader,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useConsumerEntity,
  useConsumerRecord,
  useCreateRecord,
  useSaveDraft,
  useUpdateRecord,
} from '../../hooks/useEntities';

interface EntityRecordFormProps {
  entityId: string;
  recordId?: string | null;
  mode?: 'create' | 'edit';
  onSuccess?: (record: { id: string }) => void;
  onCancel?: () => void;
}

export default function EntityRecordForm({
  entityId,
  recordId = null,
  mode = 'create',
  onSuccess,
  onCancel,
}: EntityRecordFormProps) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [formError, setFormError] = useState<string | null>(null);

  // Fetch entity definition and existing record (if editing)
  const { data: entity, isLoading: entityLoading, error: entityError } = useConsumerEntity(entityId);
  const {
    data: record,
    isLoading: recordLoading,
    error: recordError,
  } = useConsumerRecord(mode === 'edit' ? entityId : null, recordId);

  // Mutations
  const createRecord = useCreateRecord();
  const updateRecord = useUpdateRecord();
  const saveDraft = useSaveDraft();

  // Auto-save draft functionality
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const handleSaveDraft = useCallback(
    async (values: Record<string, unknown>) => {
      try {
        await saveDraft.mutateAsync({
          entityId,
          data: values,
          draftId: `${entityId}-${recordId ?? 'new'}`,
        });
        setLastSavedAt(new Date());
      } catch {
        // Silently fail draft save
      }
    },
    [entityId, recordId, saveDraft],
  );

  // Auto-save every 30 seconds while user is filling form
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (mode === 'create' && Object.keys(formValues).length > 0) {
      const timer = setTimeout(() => {
        void handleSaveDraft(formValues);
      }, 30000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [formValues, mode, handleSaveDraft]);

  const handleSubmit = async (values: Record<string, unknown>) => {
    setFormError(null);

    try {
      if (mode === 'create') {
        const result = await createRecord.mutateAsync({ entityId, data: values });
        // Clear draft after successful creation
        localStorage.removeItem(`consumer-draft-${entityId}-new`);
        if (onSuccess) {
          onSuccess(result);
        } else {
          navigate(`/records/${entityId}/${result.id}`);
        }
      } else if (recordId) {
        const result = await updateRecord.mutateAsync({ entityId, recordId, data: values });
        if (result) {
          if (onSuccess) {
            onSuccess(result);
          } else {
            navigate(`/records/${entityId}/${result.id}`);
          }
        }
      }
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save record');
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate(-1);
    }
  };

  const handleFormChange = (values: Record<string, unknown>) => {
    setFormValues(values);
  };

  const isLoading = entityLoading || (mode === 'edit' && recordLoading);
  const error = entityError || recordError;
  const isSaving = createRecord.isPending || updateRecord.isPending;

  // Filter fields based on mode
  const formFields: FieldDefinition[] = (entity?.fields ?? []).filter((field) => {
    // In create mode, show fields that should be in create form
    // In edit mode, show fields that should be in edit form
    // For now, show all non-computed fields
    return !['CALCULATED', 'ROLLUP', 'FORMULA', 'AUTO_NUMBER'].includes(field.fieldType);
  });

  if (error) {
    return (
      <GlassAlert severity="error" visible={true} sx={{ mb: 3 }}>
        Failed to load form. Please try again later.
      </GlassAlert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {mode === 'create' ? `New ${entity?.name ?? 'Record'}` : `Edit ${entity?.name ?? 'Record'}`}
        </Typography>
        {entity?.description && (
          <Typography variant="body2" color="textSecondary">
            {entity.description}
          </Typography>
        )}
      </Box>

      {/* Form Error */}
      {formError && (
        <GlassAlert severity="error" visible={true} sx={{ mb: 3 }} onClose={() => setFormError(null)}>
          {formError}
        </GlassAlert>
      )}

      {/* Draft indicator */}
      {mode === 'create' && lastSavedAt && (
        <GlassAlert severity="info" visible={true} sx={{ mb: 2 }}>
          Draft saved at {lastSavedAt.toLocaleTimeString()}
        </GlassAlert>
      )}

      {/* Form */}
      <GlassCard neonAccent="green" animate>
        {isLoading ? (
          <Box>
            {[1, 2, 3, 4].map((i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <ShimmerLoader variant="rectangular" height={56} />
              </Box>
            ))}
            <ShimmerLoader variant="rectangular" height={40} width={120} />
          </Box>
        ) : (
          <DynamicForm
            fields={formFields}
            values={mode === 'edit' && record ? record.data : {}}
            onSubmit={handleSubmit}
            onChange={handleFormChange}
            onCancel={handleCancel}
            loading={isSaving}
            submitLabel={mode === 'create' ? t('common.create') : t('consumer.entity.saveChanges')}
            cancelLabel={t('common.cancel')}
            neonAccent="green"
            columns={2}
          />
        )}
      </GlassCard>

      {/* Manual Save Draft Button for create mode */}
      {mode === 'create' && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <GlassButton
            variant="ghost"
            startIcon={<SaveIcon />}
            onClick={() => handleSaveDraft(formValues)}
            disabled={saveDraft.isPending}
          >
            {saveDraft.isPending ? t('consumer.entity.savingDraft') : t('consumer.entity.saveDraft')}
          </GlassButton>
        </Box>
      )}
    </Box>
  );
}
